import { useEffect, useState } from 'react'
import { Card, CardContent, CircularProgress } from '@mui/material'
import AssetGroupDetailsComponent from './GroupDetailsComponents/AssetGroupDetails'
import BackButton from '../../../components/BackButton'
import { useNavigate } from 'react-router-dom'
import './GroupDetailsMainComponent.css'
import LanguageStrings from '../../../i18n/locales'
import AssignedAssetListTable, { type AdvFilterParameterConditions } from './GroupDetailsComponents/AssignedAssetList'
import exportIcon from '../../../assets/exportIcon.svg'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import { Button } from '../../../components/Button'
import UnassignAssetWindow from './GroupDetailsComponents/UnassignAssetWindow'
import { type ConjuctionOptions, type UpdateGroupRequest } from '../types'
import { updateGroup, exportGroupDetailsById } from '../redux/actionCreator'
import { type UpdateGroupState } from '../redux/updateGroupSlice'
import Modal from '../../../components/Modal'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { type CheckboxDropdownMode } from '../../../components/CheckboxDropdown'
import { type UnassignedAssetListState } from '../redux/UnassignedAssetListSlice'
import { getOperationPermissions } from '../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'

const GroupDetailsMainComponentStrings = LanguageStrings().AssetGroupStrings.GroupDetailsMainComponent

const GroupDetailsMainComponent = (props: PropsFromRedux): JSX.Element => {
  const navigate = useNavigate()
  const auth = useAuth()
  const [reset, setReset] = useState(false)
  const [isEditEnabled, setIsEditEnaled] = useState(false)
  const [addedAssets, setAddedAssets] = useState<string[]>([])
  const [unselectedAssets, setUnselectedAssets] = useState<string[]>([])
  const [assetTypeId, setAssetTypeId] = useState('')
  const [description, setDescription] = useState('')
  const [descError, setDescError] = useState<boolean>(false)
  const [initialDesc, setInitialDesc] = useState<string>('')
  const [showAddAssetWindow, setShowAddAssetWindow] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showUpdatePopup, setShowUpdatePopup] = useState(false)
  const [selectedOption, setSelectedOption] = useState<CheckboxDropdownMode>('none')
  const [searchInput, setSearchInput] = useState('')
  const [selectedPages, setSelectedPages] = useState<number[]>([])
  const numAssignedAssets = useSelector((state: RootState) => state.assetAssignedList.assetAssignedList !== null ? state.assetAssignedList.assetAssignedList.total_count : 0)
  const [pagesWithIndividualSelections, setPagesWithIndividualSelections] = useState<number[]>([])
  const [fullyUnselectedPages, setFullyUnselectedPages] = useState<number[]>([])
  const [conditions, setConditions] = useState<AdvFilterParameterConditions[]>([])
  const [conjunction, setConjunction] = useState<ConjuctionOptions>('AND')
  const [isAdvanceFilterSelected, setIsAdvanceFilterSelected] = useState<boolean>(false)

  useEffect(() => {
    if (!props.operationPermissions.updated) {
      props.getOperationPermissions(auth.user?.profile.sub as string)
    }
  }, [])

  let groupName = sessionStorage.getItem('selectedGroupName')
  const groupNameID = sessionStorage.getItem('selectedGroupId')
  if (groupName === null) {
    groupName = ''
  }

  let groupId = sessionStorage.getItem('selectedGroupId')
  if (groupId === null) {
    groupId = ''
  }

  const resetComponent = (): void => {
    setIsEditEnaled(false)
    setAddedAssets([])
    setAssetTypeId('')
    setDescription('')
    setDescError(false)
    setInitialDesc('')
    setShowAddAssetWindow(false)
    setShowConfirmation(false)
    setShowUpdatePopup(false)
    setReset(!reset)
    setSearchInput('')
    setSelectedOption('none')
    setSelectedPages([])
    setPagesWithIndividualSelections([])
    setFullyUnselectedPages([])
    setConditions([])
    setConjunction('AND')
    setIsAdvanceFilterSelected(false)
  }

  const getUpdateMessage = (): JSX.Element => {
    const totalCount = props.unassignedAssetList.unassignedAssetList?.total_count ?? 0
    let assetModified
    if (selectedOption === 'all' && totalCount > 0 && totalCount - unselectedAssets.length > 0) {
      assetModified = `${totalCount - unselectedAssets.length} ${GroupDetailsMainComponentStrings.AssetsHasBeenAdded}`
    } else if ((selectedOption === 'current' || selectedOption === 'none') && addedAssets.length > 0) {
      assetModified = `${addedAssets.length} ${GroupDetailsMainComponentStrings.AssetsHasBeenAdded}`
    } else {
      assetModified = ''
    }

    const descriptionModified = GroupDetailsMainComponentStrings.DescriptionHasBeenModified
    return (
      <ul>
        {assetModified !== '' && description !== initialDesc && (
          `${assetModified} and ${descriptionModified}`
        )}
        {assetModified !== '' && description === initialDesc && (
          `${assetModified}`
        )}
        {assetModified === '' && description !== initialDesc && (
          `${descriptionModified}`
        )}
      </ul>
    )
  }

  const handleModifyGroup = (): void => {
    props.updateGroup(groupId ?? '', {
      description,
      assign_assets: [...addedAssets],
      un_assign_assets: [...unselectedAssets],
      assign_assets_select_all: selectedOption === 'all',
      un_assign_assets_select_all: false,
      search_assets_input: isAdvanceFilterSelected ? '' : searchInput,
      is_edit_for_adding_assets: true,
      conjuction: isAdvanceFilterSelected ? conjunction : '',
      conditions: conditions.map(cond => ({
        key: cond.parameter,
        operation: cond.operator,
        value: cond.value
      }))
    })
  }

  const handleUpdateGroupWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (props.updateGroupDetailsState.httpStatus === 201) {
      return (
        <Modal buttonTypeNegative='hollow' title={GroupDetailsMainComponentStrings.SuccessfullyModified} LabelPositive={GroupDetailsMainComponentStrings.Ok}
          onClickPositive={() => {
            resetComponent()
          }}
          onClose={() => { resetComponent() }}>
          <p style={{ paddingTop: '1.2em' }}>{getUpdateMessage()}</p>
        </Modal>
      )
    } else if (props.updateGroupDetailsState.isLoading) {
      return (
        <Modal title={GroupDetailsMainComponentStrings.ModifyingGroupDetails}>
          <div className='CircularprogressAmi'>
              <CircularProgress />
          </div>
        </Modal>
      )
    } else if (props.updateGroupDetailsState.httpStatus === 401) {
      return (
                <Modal titleStyle={{ paddingRight: '2em' }} width={'fit-content'} height={'fit-content'} buttonTypeNegative='hollow' LabelNegative={GroupDetailsMainComponentStrings.Close}
                    onClickNegative={() => { setShowUpdatePopup(false) }}
                    onClose={() => { setShowUpdatePopup(false) }} title={GroupDetailsMainComponentStrings.ModifyingGroupDetails}>
                    <div className='authError'>
                        <AuthError className={'errorCompInsideModal'} errorMessage="Unauthorized"/>
                    </div>
                </Modal>
      )
    } else if (props.updateGroupDetailsState.httpStatus === 403) {
      return (
                <Modal titleStyle={{ paddingRight: '2em' }} width={'fit-content'} height={'fit-content'} buttonTypeNegative='hollow' LabelNegative={GroupDetailsMainComponentStrings.Close}
                    onClickNegative={() => { setShowUpdatePopup(false) }}
                    onClose={() => { setShowUpdatePopup(false) }} title={GroupDetailsMainComponentStrings.ModifyingGroupDetails}>
                    <div className='authError'>
                        <AuthError className={'errorCompInsideModal'} errorMessage="accessForbidden"/>
                    </div>
                </Modal>
      )
    } else {
      return (
        <Modal width={'fit-content'} height={'fit-content'} buttonTypeNegative='hollow' LabelNegative={GroupDetailsMainComponentStrings.Close} LabelPositive={GroupDetailsMainComponentStrings.Retry}
          onClickNegative={() => { setShowUpdatePopup(false) }}
          onClose={() => { setShowUpdatePopup(false) }} title={GroupDetailsMainComponentStrings.ModifyingGroupDetails}
          onClickPositive={handleModifyGroup}>
          <div className='authError'>
            <AuthError className={'errorCompInsideModal'} customError={props.updateGroupDetailsState.error}/></div>
        </Modal>
      )
    }
  }

  const isUpdateEnabled = (): boolean => {
    if (descError) {
      return false
    }
    const isDescriptionModified = description !== initialDesc
    const totalCount = props.unassignedAssetList.unassignedAssetList?.total_count ?? 0
    const isewAssetsAdded = addedAssets.length > 0 || (selectedOption === 'all' && totalCount > 0 && totalCount - unselectedAssets.length > 0)
    return isDescriptionModified || isewAssetsAdded
  }

  return (
    <div style={{ width: 'calc(100vw - 3.5rem)', paddingRight: '2rem' }}>
      <div className='link-breadcrumb'>
        <span data-testid='assetManagementbreadcrumb' onClick={() => { navigate('/card') }} className='assetmgmtli'>{GroupDetailsMainComponentStrings.Asset_Management}</span>
        <span style={{ padding: '0 0.5em' }}>/</span>
        <span onClick={() => { navigate('/asset-group-list') }} className='assetmgmtli'>{GroupDetailsMainComponentStrings.Asset_Group}</span>
        <span style={{ padding: '0 0.5em' }}>/</span>
        <span>{groupName}</span>
      </div>
      <div className='topUtilityBar'>
        <BackButton />
        <span id='componentTitle' data-testid='configurations'>{groupName}</span>
        <span id='top-right-ami-utilities'>
          <span id='top-right-btn-span'>
            <img src={exportIcon} onClick={
              // istanbul ignore next
              () => { props.exportGroupDetailsById(groupName ?? '', groupNameID !== null ? groupNameID : '') }} style={{ cursor: 'pointer' }} />
          </span>
        </span>
      </div>
      <Card sx={{ borderRadius: '10px', backgroundColor: '#272727', color: '#F0F0F0 !important', padding: '25px' }}>
        <h4>{GroupDetailsMainComponentStrings.ASSET_GROUP_DETAILS}</h4>
        <CardContent>
          <AssetGroupDetailsComponent
            setAssetTypeId={setAssetTypeId}
            isEditEnabled={isEditEnabled}
            description={description}
            setDescription={setDescription}
            descError={descError}
            setDescError={setDescError}
            setInitialDesc={setInitialDesc}
            reset={reset}
          />
          <div>
            <div id='assigned-asset-list-group'>
              <h5>{GroupDetailsMainComponentStrings.Assigned_Asset_List} <span className='config-list-box'>{numAssignedAssets} {GroupDetailsMainComponentStrings.Assets}</span></h5>
            </div>
            <AssignedAssetListTable
              setShowAddAssetWindow={setShowAddAssetWindow}
              isEditEnabled={isEditEnabled}
              reset={reset}
              resetComponent={resetComponent}
            />
          </div>
        </CardContent>
      </Card>
      { props.operationPermissions.permissions.can_modify_assetgroup &&
        <div style={{ width: '100%', backgroundColor: '#272727', display: 'flex', marginTop: '20px', justifyContent: 'space-between', alignItems: 'center', padding: '6px 24px' }}>
          <div style={{ justifyContent: 'flex-start' }}>
            {
              isEditEnabled &&
              <Button type='borderless' dataTestId='cancel-btn' onClick={() => { setIsEditEnaled(false) }}>{GroupDetailsMainComponentStrings.Cancel}</Button>
            }
          </div>
          <div style={{ justifyContent: 'flex-end' }}>
            {
              isEditEnabled
                ? <Button primaryColor={!isUpdateEnabled() ? '#B0B0B0' : undefined} disabled={!isUpdateEnabled()} dataTestId='update-btn' onClick={() => { setShowConfirmation(true) }}>{GroupDetailsMainComponentStrings.Update}</Button>
                : <Button dataTestId='modify-btn' onClick={() => { setIsEditEnaled(true) }}>{GroupDetailsMainComponentStrings.Modify}</Button>
            }
          </div>
        </div>
      }
      {
        showAddAssetWindow
          ? <>
            <UnassignAssetWindow
              fullyUnselectedPages={fullyUnselectedPages} setFullyUnselectedPages={setFullyUnselectedPages}
              pagesWithIndividualSelections={pagesWithIndividualSelections} setPagesWithIndividualSelections={setPagesWithIndividualSelections}
              unselectedAssets={unselectedAssets} setUnselectedAssets={setUnselectedAssets}
              searchInput={searchInput} setSearchInput={setSearchInput} selectedOption={selectedOption} setSelectedOption={setSelectedOption}
              addedAssets={addedAssets}
              setAddedAssets={setAddedAssets}
              assetTypeId={assetTypeId}
              setShowAddAssetWindow={setShowAddAssetWindow}
              onClose={() => { setShowAddAssetWindow(false) }}
              selectedPages={selectedPages}
              setSelectedPages={setSelectedPages}
              conditions={conditions}
              setConditions={setConditions}
              conjuction={conjunction}
              setConjunction={setConjunction}
              setIsAdvanceFilterSelected={setIsAdvanceFilterSelected}
            />
          </>
          : <></>
      }
      { showConfirmation &&
        <Modal buttonTypeNegative='hollow' title={GroupDetailsMainComponentStrings.ModifyingGroupDetails} LabelNegative={GroupDetailsMainComponentStrings.Cancel} LabelPositive={GroupDetailsMainComponentStrings.Update} onClickPositive={() => {
          setShowConfirmation(false)
          setShowUpdatePopup(true)
          handleModifyGroup()
        }} onClose={() => { setShowConfirmation(false) }} onClickNegative={() => { setShowConfirmation(false) }}>
          <p style={{ paddingTop: '1.2em' }}>{GroupDetailsMainComponentStrings.DoYouWantToModifyGroup}</p>
        </Modal>
      }
      {
        showUpdatePopup &&
        handleUpdateGroupWithFailure()
      }
    </div>
  )
}

interface DispatchToProps {
  updateGroup: (groupId: string, updateGroupRequest: UpdateGroupRequest) => void
  exportGroupDetailsById: (assetGroup: string, assetGroupId: string) => void
  getOperationPermissions: (sub: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  updateGroup: (groupId: string, updateGroupRequest: UpdateGroupRequest) => dispatch(updateGroup(groupId, updateGroupRequest)),
  exportGroupDetailsById: (assetGroup: string, assetGroupId: string) => dispatch(exportGroupDetailsById(assetGroup, assetGroupId)),
  getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
})

interface StateToProps {
  updateGroupDetailsState: UpdateGroupState
  unassignedAssetList: UnassignedAssetListState
  operationPermissions: OperationPermissionState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  updateGroupDetailsState: state.updateGroupDetailsState,
  unassignedAssetList: state.UnassignedAssetList,
  operationPermissions: state.operationPermissions
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(GroupDetailsMainComponent)
